import Vue from 'vue'

// On phones the tiny corner "toast" notification is easy to miss — it's small,
// sits at the very top of the viewport, and can get covered by the browser
// chrome/keyboard. This wraps the existing `this.$toast({...})` calls (used
// consistently across the whole app) so that on mobile viewports they render
// as a prettier SweetAlert2 toast instead, while leaving desktop behaviour
// (vue-toastification) completely untouched.
//
// IMPORTANT: this must stay a SweetAlert2 "toast" (toast: true), never a
// centered modal (toast: false). The centered/backdrop mode adds a
// `swal2-height-auto` class to <html> and toggles body scroll-locking, which
// fights with this app's own fixed navbar/sidebar height calculations and
// was causing a full viewport-height blank gap above the page content on
// mobile (every page fires a `$toast(...)` on mount, e.g. "Fetching data
// success", so it was tripping on nearly every navigation). Toast mode has
// no backdrop and doesn't touch document height, so it's safe to fire on
// every page load.

const MOBILE_BREAKPOINT = 768

const isMobileViewport = () => typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT

const VARIANT_TO_ICON = {
  success: 'success',
  danger: 'error',
  error: 'error',
  warning: 'warning',
  info: 'info',
  primary: 'info',
  secondary: 'info',
}

export default function installMobileToastBridge() {
  const originalToast = Vue.prototype.$toast

  if (typeof originalToast !== 'function') return

  const patchedToast = function patchedToast(payload, options) {
    const props = payload && payload.props

    if (!isMobileViewport() || !props || typeof this.$swal !== 'function') {
      return originalToast.call(this, payload, options)
    }

    const icon = VARIANT_TO_ICON[props.variant] || 'info'
    // This renders as a single-line compact toast (see the .geopro-mobile-alert
    // rules in style.scss), so collapse title+text into one short line rather
    // than showing both — the message text is more useful than a generic
    // heading like "Success!", so prefer it when present.
    const message = props.text || props.title || ''

    return this.$swal({
      icon,
      title: message,
      toast: true,
      position: 'top',
      showConfirmButton: false,
      timer: 2600,
      timerProgressBar: true,
      customClass: {
        popup: 'geopro-mobile-alert',
      },
    })
  }

  // Preserve any extra helper methods vue-toastification attaches to the
  // toast function (e.g. clear/dismiss) so nothing else breaks.
  Object.keys(originalToast).forEach(key => {
    if (typeof originalToast[key] === 'function') {
      patchedToast[key] = originalToast[key].bind(originalToast)
    }
  })

  Vue.prototype.$toast = patchedToast
}
